import ReceiptStrategy from './ReceiptStrategy';
import GeneralReceiptForm from '../components/GeneralReceiptForm';
import { createReceipt } from '../../../../usecases/receipts-usecase';
import { formatMoney } from '../../../../utils/formating';
import { lang } from '../../../../utils/langs';

/**
 * Estrategia para crear recibos de caja generales.
 * Implementa la lógica actual de recibos generales.
 * 
 * @extends ReceiptStrategy
 */
class GeneralReceiptStrategy extends ReceiptStrategy {
    /**
     * Retorna el componente de formulario para recibos generales.
     * 
     * @returns {Function} Componente GeneralReceiptForm
     */
    getFormComponent() {
        return GeneralReceiptForm;
    }

    /**
     * Valida los datos del formulario de recibo general.
     * 
     * @param {Object} data - Datos del formulario
     * @param {string} data.description - Descripción del recibo
     * @param {string} data.receipt_type_id - ID del tipo de recibo
     * @param {Array} data.payment_methods - Métodos de pago seleccionados
     * @returns {Object} { isValid: boolean, errors: Object }
     */
    validate(data) {
        const errors = {};

        // Validar descripción
        if (!data.description || data.description.trim() === '') {
            errors.description = true;
        }

        // Validar tipo de recibo
        if (!data.receipt_type_id || data.receipt_type_id === '') {
            errors.receipt_type_id = true;
        }

        // Validar métodos de pago
        if (!data.payment_methods || data.payment_methods.length === 0) {
            errors.payment_methods = 'Debe seleccionar al menos un método de pago';
        } else {
            const totalAmount = data.payment_methods.reduce((sum, method) => sum + parseFloat(method.value || 0), 0);
            if (totalAmount <= 0) {
                errors.payment_methods = 'El monto total debe ser mayor a cero';
            }
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    /**
     * Envía el recibo general al backend.
     * 
     * @param {Object} data - Datos del recibo
     * @param {Object} context - Contexto con receiptTypes
     * @returns {Promise<Object>} Respuesta del servidor
     */
    async submit(data, context) {
        const { receiptTypes } = context;

        // Calcular monto total
        const totalAmount = data.payment_methods.reduce((sum, method) => sum + parseFloat(method.value || 0), 0);

        // Obtener info del tipo de recibo
        const receipt_info = receiptTypes?.filter(item => item.id === parseInt(data.receipt_type_id));

        const dataToSend = {
            description: data.description,
            receipt_type_id: parseInt(data.receipt_type_id),
            amount: parseFloat(totalAmount),
            payment_methods: data.payment_methods,
            receipt_info: receipt_info?.[0]
        };

        const response = await createReceipt(dataToSend);

        // Retornar datos combinados para impresión
        return {
            ...dataToSend,
            data: response.data
        };
    }

    /**
     * Genera el contenido HTML para impresión de recibo general.
     * 
     * @param {Object} data - Datos del recibo
     * @param {Object} profile - Datos de la empresa
     * @returns {string} HTML para imprimir
     */
    generatePrintContent(data, profile) {
        let methodsHtml = '';
        data?.payment_methods?.forEach((method) => {
            methodsHtml += `<tr><td>${lang('es', method.method)}</td><td style="text-align: right;">${formatMoney(method.value)}</td></tr>`;
        });

        return `
            <div id="print-content" style="
                width: 100%;
                min-width: 58mm;
                max-width: 80mm;
                font-family: 'Courier New', monospace;
                font-size: 11px;
                padding: 8px;
                color: #000;
                margin: 0 auto;
            ">
                <div style="text-align: center;">
                    <strong>${profile?.companyName || 'Empresa'}</strong><br/>
                    ${profile?.email || ''}<br/>
                    ${profile?.phone || ''}<br/>
                    ${profile?.address || ''}<br/>
                    -------------------------------
                </div>
        
                <div style="margin-top: 8px;">
                    <div><strong>Info Recibo:</strong> ${data?.receipt_info?.name || 'N/A'}</div>
                    <div><strong>Descripción:</strong> ${data?.description || ''}</div>
                    <div><strong>Fecha:</strong> ${new Date().toLocaleString()}</div>
                </div> 
        
                <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
        
                <table style="width: 100%;">
                    <tr><td><strong>Total Recibo</strong></td><td style="text-align: right;"><strong>${formatMoney(data.amount)}</strong></td></tr>
                </table>
        
                <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
        
                <table style="width: 100%;">
                    <tr><td colspan="2"><strong>Métodos de pago:</strong></td></tr>
                    ${methodsHtml}
                </table> 
        
                <div style="text-align: center; margin-top: 10px;">
                    ¡Gracias por tu preferencia!
                </div> 
            </div>
        `;
    }

    /**
     * Retorna el estado inicial del formulario de recibo general.
     * 
     * @returns {Object} Estado inicial
     */
    getInitialState() {
        return {
            description: '',
            receipt_type_id: '',
            payment_methods: []
        };
    }

    /**
     * Retorna el título del botón de envío.
     * 
     * @returns {string} Título del botón
     */
    getSubmitButtonTitle() {
        return 'Crear Recibo';
    }

    /**
     * Retorna el nombre del evento a disparar después de crear.
     * 
     * @returns {string} Nombre del evento
     */
    getUpdateEventName() {
        return 'update-receipts';
    }
}

export default GeneralReceiptStrategy;
